import { atrFromBars } from '../indicators/atr';
import { dailyEmaMapped } from '../indicators/ema';
import { fairValueGapAt, fairValueGaps } from '../indicators/fvg';
import { parseClock, sessionBoxes, sessionRangeSeries, sessionSeries, sessionWindowsFromParams } from '../indicators/session';

function formatLevel(n) {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const digits = abs >= 1 ? 2 : 4;
  return n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function checkFvgPosition(fvg, rangeHigh, rangeLow) {
  if (!fvg || !Number.isFinite(rangeHigh) || !Number.isFinite(rangeLow) || rangeHigh <= rangeLow) return false;
  const span = rangeHigh - rangeLow;
  const fib75 = rangeLow + span * 0.7;
  const fib25 = rangeLow + span * 0.25;
  return (fvg.direction === 'Long' && fvg.top < fib75) || (fvg.direction === 'Short' && fvg.bottom > fib25);
}

function emaChip(price, ema, fvg) {
  if (!Number.isFinite(price) || !Number.isFinite(ema)) {
    return { id: 'ema', label: 'vs EMA', pass: false, tone: 'muted' };
  }
  const under = price < ema;
  const label = `${formatLevel(price)} ${under ? 'under' : 'above'} ${formatLevel(ema)}`;
  const aligned = !fvg ? false : (fvg.direction === 'Long' ? !under : under);
  return { id: 'ema', label, pass: aligned, tone: aligned ? 'pass' : 'warn' };
}

function rangeChip(fvg, rangeHigh, rangeLow) {
  if (!Number.isFinite(rangeHigh) || !Number.isFinite(rangeLow) || rangeHigh <= rangeLow) {
    return { id: 'range', label: 'vs range', pass: false, tone: 'muted' };
  }
  const band = `${formatLevel(rangeLow)}–${formatLevel(rangeHigh)}`;
  const pass = checkFvgPosition(fvg, rangeHigh, rangeLow);
  if (!fvg) {
    return { id: 'range', label: `Range ${band}`, pass: false, tone: 'muted' };
  }
  return {
    id: 'range',
    label: pass ? `In range ${band}` : `Outside ${band}`,
    pass,
    tone: pass ? 'pass' : 'warn',
  };
}

function sessionLimitExpireMinutes(session, params) {
  const morning = Boolean(session?.isMorningSession) || (Number.isFinite(session?.hour) && session.hour < 9);
  const end = parseClock(
    morning ? params.morningEnd : params.afternoonEnd,
    morning ? { hour: 7, minute: 59 } : { hour: 11, minute: 59 },
  );
  return (end.hour * 60 + end.minute + 1) % (24 * 60);
}

export const macroBullet = {
  id: 'macro-bullet',
  name: 'Macro Bullet',
  indicators: ['FVG', 'EMA', 'ATR'],
  chartOverlays(params = {}) {
    return [
      { key: 'ema', label: `Daily EMA ${params.emaPeriod ?? 30}`, colorKey: 'warning', lineWidth: 2 },
    ];
  },
  paramSchema: [
    { key: 'morningStart', label: 'Morning start', type: 'time', default: '06:50', section: 'Session (PT)' },
    { key: 'morningEnd', label: 'Morning end', type: 'time', default: '07:59', section: 'Session (PT)' },
    { key: 'biasMorningEnd', label: 'AM bias end', type: 'time', default: '07:20', section: 'Session (PT)' },
    { key: 'afternoonStart', label: 'Afternoon start', type: 'time', default: '10:50', section: 'Session (PT)' },
    { key: 'afternoonEnd', label: 'Afternoon end', type: 'time', default: '11:59', section: 'Session (PT)' },
    { key: 'biasAfternoonEnd', label: 'PM bias end', type: 'time', default: '11:20', section: 'Session (PT)' },
    { key: 'fvgMinSize', label: 'FVG min', type: 'number', default: 2.5, min: 0, max: 50, step: 0.1, section: 'Setup' },
    { key: 'fvgMaxSize', label: 'FVG max', type: 'number', default: 20, min: 0.5, max: 200, step: 0.5, section: 'Setup' },
    { key: 'emaPeriod', label: 'Daily EMA', type: 'number', default: 30, min: 2, max: 200, step: 1, section: 'Setup' },
    { key: 'atrPeriod', label: 'ATR period', type: 'number', default: 14, min: 2, max: 200, step: 1, section: 'Setup' },
    { key: 'atrMultiple', label: 'ATR stop', type: 'number', default: 1.5, min: 0.1, max: 10, step: 0.1, section: 'Setup' },
    { key: 'tp2R', label: 'TP2 R', type: 'number', default: 2, min: 0.5, max: 10, step: 0.1, section: 'Setup' },
  ],

  prepare(bars, params) {
    const session = sessionSeries(bars, sessionWindowsFromParams(params));
    const range = sessionRangeSeries(bars, session);
    return {
      ind: {
        atr: atrFromBars(bars, params.atrPeriod),
        ema: dailyEmaMapped(bars, params.emaPeriod),
        fvg: fairValueGaps(bars, { minSize: params.fvgMinSize, maxSize: params.fvgMaxSize }),
        rangeHigh: range.rangeHigh,
        rangeLow: range.rangeLow,
        sessionBoxes: sessionBoxes(bars, session),
      },
      session,
      state: {
        sessionKey: null,
        alreadyTraded: false,
      },
    };
  },

  explain(ctx) {
    const { i, bar, bars, ind, params, session } = ctx;
    const fvg = fairValueGapAt(bars, i, { minSize: params.fvgMinSize, maxSize: params.fvgMaxSize });
    const rangeHigh = ind.rangeHighNow ?? ind.rangeHigh?.[i];
    const rangeLow = ind.rangeLowNow ?? ind.rangeLow?.[i];
    return [
      { id: 'session', label: 'In session', pass: Boolean(session?.isTradingSession) },
      { id: 'bias', label: 'Bias window', pass: Boolean(session?.determiningBias) },
      { id: 'fvg', label: 'FVG forming', pass: Boolean(fvg?.direction) },
      { id: 'size', label: 'Size OK', pass: Boolean(fvg) },
      emaChip(bar.close, ind.emaNow ?? ind.ema?.[i], fvg),
      rangeChip(fvg, rangeHigh, rangeLow),
    ];
  },

  onBar(ctx) {
    const { i, bar, bars, ind, params, position, session, state } = ctx;
    if (!session) return [];

    if (session.sessionKey !== state.sessionKey) {
      state.sessionKey = session.sessionKey;
      state.alreadyTraded = false;
    }

    if (state.alreadyTraded || position) return [];
    if (!session.determiningBias) return [];

    const fvg = fairValueGapAt(bars, i, { minSize: params.fvgMinSize, maxSize: params.fvgMaxSize });
    if (!fvg) return [];

    const ema = ind.emaNow ?? ind.ema?.[i];
    const atr = ind.atrNow ?? ind.atr?.[i];
    const rangeHigh = ind.rangeHighNow ?? ind.rangeHigh?.[i];
    const rangeLow = ind.rangeLowNow ?? ind.rangeLow?.[i];
    const aboveEma = Number.isFinite(ema) && bar.close > ema;
    const belowEma = Number.isFinite(ema) && bar.close < ema;
    const vsEma = fvg.direction === 'Long' ? aboveEma : belowEma;
    const vsRange = checkFvgPosition(fvg, rangeHigh, rangeLow);
    if (!vsEma || !vsRange || !Number.isFinite(atr)) return [];

    const side = fvg.direction === 'Long' ? 'long' : 'short';
    const limitPrice = side === 'long' ? fvg.top : fvg.bottom;
    const expireAtMinutes = sessionLimitExpireMinutes(session, params);
    state.alreadyTraded = true;

    return [{
      type: 'enter',
      orderType: 'limit',
      side,
      limitPrice,
      atr,
      atrMultiple: params.atrMultiple,
      expireDateKey: session.dateKey,
      expireAtMinutes,
      takeProfits: [
        { id: 'tp1', rMultiple: 1, qtyPct: 0.5 },
        { id: 'tp2', rMultiple: params.tp2R, qtyPct: 0.5 },
      ],
    }];
  },
};

export default macroBullet;
